import { MainScreen } from '@euler/app/components/MainShell';
import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { EntityCardPickerScreen } from '@euler/app/components/entity-card-picker/EntityCardPickerScreen';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { AiChatPage } from '@euler/app/flows/ai-suite/AiChatPage';
import { ApdmCampaignDetailPage } from '@euler/app/flows/apdm/campaign-detail/ApdmCampaignDetailPage';
import { ApdmCampaignWizardPage } from '@euler/app/flows/apdm/campaigns/wizard/ApdmCampaignWizardPage';
import { ApdmCouponRedemptionPage } from '@euler/app/flows/apdm/coupons/ApdmCouponRedemptionPage';
import { ApdmDashboardPage } from '@euler/app/flows/apdm/dashboard/ApdmDashboardPage';
import { ApdmImportPage } from '@euler/app/flows/apdm/imports/ApdmImportPage';
import { AuthScreen } from '@euler/app/flows/auth/Auth';
import { AuthContext } from '@euler/app/flows/auth/context';
import { AppSelectionScreen } from '@euler/app/flows/auth/pages/AppSelection';
import { BiCardPage } from '@euler/app/flows/bi-dashboards/BiCardPage';
import { BiDashboardListPage } from '@euler/app/flows/bi-dashboards/BiDashboardListPage';
import { BiDashboardPage } from '@euler/app/flows/bi-dashboards/BiDashboardPage';
import { BiDatasetLibraryPage } from '@euler/app/flows/bi-dashboards/BiDatasetLibraryPage';
import { BiDatasetPage } from '@euler/app/flows/bi-dashboards/BiDatasetPage';
import { BiExportListPage } from '@euler/app/flows/bi-dashboards/BiExportListPage';
import { BiHomePage } from '@euler/app/flows/bi-dashboards/BiHomePage';
import { ChatFlow } from '@euler/app/flows/chat/ChatFlow';
import { CoachingFlow } from '@euler/app/flows/coaching';
import { AlertScreen } from '@euler/app/flows/common/AlertScreen';
import { AppSwitchScreen } from '@euler/app/flows/common/AppSwitchScreen';
import { ConfirmScreen } from '@euler/app/flows/common/ConfirmScreen';
import { PlateNoRecognizeResultScreen } from '@euler/app/flows/common/PlateNoRecognizeResultScreen';
import { PlateNoScreen } from '@euler/app/flows/common/PlateNoScreen';
import { PlateVinScanScreen } from '@euler/app/flows/common/PlateVinScanScreen';
import { PromptScreen } from '@euler/app/flows/common/PromptScreen';
import { QrcodeScanScreen } from '@euler/app/flows/common/QrcodeScanScreen';
import { SelectMemberPage } from '@euler/app/flows/common/SelectMemberPage';
import { SignatureScreen } from '@euler/app/flows/common/SignatureScreen';
import { VinRecognizeResultScreen } from '@euler/app/flows/common/VinRecognizeResultScreen';
import { EmbeddedWebViewPage } from '@euler/app/flows/common/embedded-webview/EmbeddedWebViewPage';
import { ErrorScreen } from '@euler/app/flows/error/Error';
import { NotFoundScreen } from '@euler/app/flows/error/NotFound';
import { LeadDetailFlow } from '@euler/app/flows/leads';
import { CameraScreen } from '@euler/app/flows/media/CameraScreen';
import { ContinuousCapturePreviewScreen } from '@euler/app/flows/media/ContinuousCapturePreviewScreen';
import { MediaPreviewScreen } from '@euler/app/flows/media/MediaPreviewScreen';
import { AddCustomTagScreen } from '@euler/app/flows/media/annotation/AddCustomTagScreen';
import { ImageAnnotationScreen } from '@euler/app/flows/media/annotation/ImageAnnotationScreen';
import { MultipleImageAnnotationScreen } from '@euler/app/flows/media/annotation/MultipleImageAnnotationScreen';
import { MediaPickerScreen } from '@euler/app/flows/media/picker/MediaPickerScreen';
import { EmbeddedWebExperiencePage } from '@euler/app/flows/misc/EmbeddedWebExperiencePage';
import { OrderFlow } from '@euler/app/flows/order/OrderFlow';
import { OrderContextProvider } from '@euler/app/flows/order/functions/OrderContext';
import { HistoricalServiceInfoPendingIssuesScreen } from '@euler/app/flows/order/pages/OrderScreen/components/HistoricalServiceInfo/PendingIssuesScreen';
import { PosterStudioPage } from '@euler/app/flows/poster-studio/PosterStudioPage';
import { ProductScreens } from '@euler/app/flows/products/screens';
import { ServiceRecordFlow } from '@euler/app/flows/record/ServiceRecordFlow';
import { ReportScreens } from '@euler/app/flows/report/ReportScreens';
import { ReportScreen } from '@euler/app/flows/report/pages/Report';
import { ReservationDetailFlow } from '@euler/app/flows/reservations';
import { SearchOrdersFlow } from '@euler/app/flows/search';
import { TaskFlow } from '@euler/app/flows/task/TaskFlow';
import { WorkshopBoardPage } from '@euler/app/flows/workshop-board/WorkshopBoardPage';
import { withStoreFeature } from '@euler/components/entitlements/withStoreFeature';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { StoreFeature } from '@euler/model/enum';
import type { PlaygroundPage } from '@euler/playground/Index';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { createStackNavigator } from '@react-navigation/stack';
import { StatusBar } from 'expo-status-bar';
import { memo, useContext } from 'react';
import { useTheme } from 'styled-components/native';
import { Routes as PresentationRoutes } from './Routes.tv';

const MainStack = createStackNavigator();

export const ModalScreens = {
  _AppSelection: AppSelectionScreen,
  _mediaPreview: MediaPreviewScreen,
  _camera: CameraScreen,
  _continuousCapturePreview: ContinuousCapturePreviewScreen,
  _qrcodeScan: QrcodeScanScreen,
  _imageAnnotation: ImageAnnotationScreen,
  _imageAnnotationMulti: MultipleImageAnnotationScreen,
  _mediaPicker: MediaPickerScreen,
  _addCustomTagAnnotation: AddCustomTagScreen,
  _plate: PlateNoScreen,
  _error: ErrorScreen,
  _vehicleInfoScanner: PlateVinScanScreen,
  _plateRecognizeResult: PlateNoRecognizeResultScreen,
  _vinRecognizeResult: VinRecognizeResultScreen,
  _search: SearchOrdersFlow,
  _order: OrderFlow,
  _prompt: PromptScreen,
  _alert: AlertScreen,
  _confirm: ConfirmScreen,
  _selectMember: SelectMemberPage,
  _entityCardPicker: EntityCardPickerScreen,
  _pendingIssues: HistoricalServiceInfoPendingIssuesScreen,
  _embeddedWebExperience: EmbeddedWebExperiencePage,
  _webView: EmbeddedWebViewPage,
  // App-level lead detail (a leads stack opened at `detail`) so an in-feed
  // "view lead" pushes onto the root stack — back returns to the feed thread.
  _lead: LeadDetailFlow,
  // Same reasoning as `_lead`: opened at `detail` on the root stack so back
  // returns to wherever the reservation was opened from.
  _reservation: ReservationDetailFlow,
  // A chat room is a full-screen surface, so it rides on the ROOT stack (over
  // the tab bar) rather than inside the inbox tab — see `ChatFlow`. Push it
  // with `useOpenChatConversation`/`pushChatConversation`.
  _chat: ChatFlow,
};

export const AppScreens = {
  Main: MainScreen,
  // Over the tabs, like a task: the chat takes the whole display.
  AiChat: AiChatPage,
  ApdmImport: ApdmImportPage,
  ApdmDashboard: ApdmDashboardPage,
  ApdmCampaign: ApdmCampaignDetailPage,
  ApdmCampaignWizard: ApdmCampaignWizardPage,
  ApdmCouponRedemption: ApdmCouponRedemptionPage,
  // The BI entry: replaces itself with the default dashboard or the list.
  BiHome: BiHomePage,
  BiDashboards: BiDashboardListPage,
  BiDashboard: BiDashboardPage,
  BiDashboardCard: BiCardPage,
  BiDatasets: BiDatasetLibraryPage,
  BiExports: BiExportListPage,
  BiDataset: BiDatasetPage,
  // The poster studio, framed: its own pages navigate inside the frame.
  PosterStudio: withStoreFeature(PosterStudioPage, StoreFeature.PosterStudio),
  NotFound: NotFoundScreen,
  Task: TaskFlow,
  // The workshop on a wall: full screen, black, over everything.
  WorkshopBoard: withStoreFeature(
    WorkshopBoardPage,
    StoreFeature.OrderReception,
  ),
  // A standalone top-level stack: a practice session is a self-contained
  // activity with its own lifecycle, so its back behaviour must not depend on
  // whichever screen launched it.
  Coaching: withStoreFeature(CoachingFlow, StoreFeature.Coaching),
  Report: ReportScreen,
  ServiceRecord: ServiceRecordFlow,
  ...ReportScreens,
  ...ModalScreens,
  _appSwitch: AppSwitchScreen,
  ...ProductScreens,
};

type DevScreenMap = { Playground: typeof PlaygroundPage };

/**
 * Dev builds only: release builds neither register these screens nor link
 * to them (see `linking.normal.ts`), so `/playground` is NotFound there.
 * Required rather than imported, so the dead branch drops the playground
 * and its fixtures from a release bundle.
 */
export const DevScreens: DevScreenMap | null = __DEV__
  ? {
      Playground: (
        require('@euler/playground/Index') as typeof import('@euler/playground/Index')
      ).PlaygroundPage,
    }
  : null;

export const AppLandscapeScreens = {
  Signature: SignatureScreen,
};

export type AppNavParams = ScreenToParams<typeof AppScreens & DevScreenMap>;
export type AppLandscapeNavParams = ScreenToParams<typeof AppLandscapeScreens>;
export type ModalNavParams = ScreenToParams<typeof ModalScreens>;

export const NormalRoutes = memo(() => {
  const authContext = useContext(AuthContext);
  const theme = useTheme();
  const [{ isAuthenticated }] = useBehaviorSubject(authContext.authState$);

  if (isAuthenticated === undefined) return null;

  return (
    <OrderContextProvider>
      <StatusBar hidden={false} />
      <MainStack.Navigator
        screenOptions={{
          headerBackImage: () => <BackIcon />,
          headerBackTitleVisible: false,
          headerShown: false,
          cardStyle: {
            backgroundColor: theme.page.background,
          },
        }}
      >
        {isAuthenticated ? (
          <MainStack.Group screenOptions={{ headerShown: true }}>
            {spreadScreens(MainStack.Screen, AppScreens)}
            {DevScreens ? spreadScreens(MainStack.Screen, DevScreens) : null}
          </MainStack.Group>
        ) : (
          <MainStack.Group screenOptions={{ headerShown: false }}>
            <MainStack.Screen name="Auth" component={AuthScreen} />
          </MainStack.Group>
        )}
        <MainStack.Group
          screenOptions={{
            headerBackImage: () => <BackIcon />,
            headerBackTitleVisible: false,
            headerShown: true,
            cardStyle: {
              backgroundColor: theme.page.background,
            },
          }}
        >
          {spreadScreens(MainStack.Screen, AppLandscapeScreens)}
        </MainStack.Group>
      </MainStack.Navigator>
    </OrderContextProvider>
  );
});

export const Routes = memo(() => {
  const presentationMode = usePresentationMode();
  if (presentationMode === 'presentation') {
    return <PresentationRoutes />;
  }
  return <NormalRoutes />;
});
